import { API_BASE_URL } from '../config/settings';
import type { FileType } from '../components/Editor';

export type ItemType = 'Notes' | 'Resource' | 'Content';

export interface DocumentMeta {
  id: string;
  title: string;
  tags: string[];
  created_at: number;
  updated_at: number;
  version: number;
  source: 'local' | 'gdrive' | 'import';
  isFavorite?: boolean;
  isArchived?: boolean;
  workspaceId?: string;
  folderId?: string | null;      // Folder assignment
  fileType?: FileType;
  isKnowledgeEnabled?: boolean;
  // Master fields for filtering/search
  type?: ItemType;               // Notes, Resource, Content
  project?: string;              // Project name (for tree search)
  resourceCategory?: string;     // Category of Resources
  customFields?: Record<string, unknown>;  // User-defined fields (gdrive_file_id, summary, url, etc.)
  // Optional content field (populated when needed for previews/AI operations)
  content?: string;
  // Human Review fields
  humanReview?: boolean;         // true = reviewed in UI, false = needs review
  completionScore?: number;      // 0-100, calculated based on filled fields
}

export interface Workspace {
  id: string;
  name: string;
  color: string;
  created_at: number;
  isDefault?: boolean;
}

export interface Folder {
  id: string;
  name: string;
  parentId: string | null;
  created_at: number;
  order: number;
}

export interface Document {
  meta: DocumentMeta;
  content: string;
}

export interface SearchIndexEntry {
  title: string;
  snippet: string;
  tags: string[];
}

export interface SearchIndex {
  [docId: string]: SearchIndexEntry;
}

export interface SyncCheckResponse {
  documents: {
    id: string;
    updated_at: number;
    version: number;
  }[];
}

export interface GDriveTokens {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  tokenVersion?: number;  // For optimistic locking during refresh
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

// Search result from backend
export interface SearchResult {
  id: string;
  title: string;
  snippet: string;
  tags: string[];
  score?: number;
}

// Knowledge bucket types (for semantic search)
export type KnowledgeBucket = 'pdfs' | 'images' | 'clips' | 'general';

// File upload bucket types (PARA folders)
export type FileBucket = 'projects' | 'areas' | 'resources' | 'archives';

// AI Search result
export interface AISearchResult {
  answer: string;
  sources: Array<{
    id: string;
    title: string;
    excerpt: string;
    score: number;
    bucket?: string;
    filename?: string;
  }>;
}

class Api {
  private getHeaders(): HeadersInit {
    // Add auth headers if needed
    return {
      'Content-Type': 'application/json'
    };
  }

  async listDocuments(): Promise<ApiResponse<DocumentMeta[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/docs`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async getDocument(id: string): Promise<ApiResponse<Document>> {
    try {
      const response = await fetch(`${API_BASE_URL}/docs/${id}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async createDocument(data: { title?: string; content?: string; tags?: string[]; fileType?: FileType }): Promise<ApiResponse<Document>> {
    try {
      const response = await fetch(`${API_BASE_URL}/docs`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  // Create document ensuring unique title (client-side logic moved to server ideally, but handling here for now)
  async createDocumentWithUniqueTitle(data: { title: string; content?: string }): Promise<ApiResponse<Document>> {
    // Check if title exists
    const list = await this.listDocuments();
    if (list.success && list.data) {
      const existingTitles = new Set(list.data.map(d => d.title));
      let title = data.title;
      let counter = 2;
      while (existingTitles.has(title)) {
        title = `${data.title} ${counter}`;
        counter++;
      }
      data.title = title;
    }
    return this.createDocument(data);
  }

  async updateDocument(id: string, data: Partial<DocumentMeta> & { content?: string }): Promise<ApiResponse<Document>> {
    try {
      const response = await fetch(`${API_BASE_URL}/docs/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async deleteDocument(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/docs/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async checkSync(): Promise<ApiResponse<SyncCheckResponse>> {
    try {
      const response = await fetch(`${API_BASE_URL}/sync/check`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async checkSyncDocument(id: string): Promise<ApiResponse<{ version: number }>> {
    try {
      const response = await fetch(`${API_BASE_URL}/sync/check/${id}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  // Folders
  async listFolders(): Promise<ApiResponse<Folder[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async createFolder(name: string, parentId?: string | null): Promise<ApiResponse<Folder>> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ name, parentId })
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async updateFolder(id: string, data: { name?: string; parentId?: string | null; order?: number }): Promise<ApiResponse<Folder>> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async deleteFolder(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  // Workspaces
  async listWorkspaces(): Promise<ApiResponse<Workspace[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/workspaces`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async createWorkspace(name: string, color: string): Promise<ApiResponse<Workspace>> {
    try {
      const response = await fetch(`${API_BASE_URL}/workspaces`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ name, color })
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async updateWorkspace(id: string, data: { name?: string; color?: string; isDefault?: boolean }): Promise<ApiResponse<Workspace>> {
    try {
      const response = await fetch(`${API_BASE_URL}/workspaces/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async deleteWorkspace(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/workspaces/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  // Upload image
  async uploadImage(file: File): Promise<ApiResponse<{ url: string; filename: string }>> {
    try {
      // Convert to base64
      const reader = new FileReader();
      return new Promise((resolve) => {
        reader.onloadend = async () => {
          const base64 = (reader.result as string).split(',')[1];
          const response = await fetch(`${API_BASE_URL}/images/upload`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              image: base64,
              filename: file.name,
              mimeType: file.type
            })
          });
          resolve(await response.json());
        };
        reader.readAsDataURL(file);
      });
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async uploadKnowledgeFile(file: File, bucket: FileBucket, subFolder?: string): Promise<ApiResponse<{ id: string; url: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', bucket);
    if (subFolder) formData.append('subFolder', subFolder);

    try {
      const response = await fetch(`${API_BASE_URL}/knowledge/files/upload`, {
        method: 'POST',
        body: formData
        // Content-Type header is set automatically with boundary for FormData
      });
      return await response.json();
    } catch (error) {
      console.error('File upload error:', error);
      return { success: false, error: 'Network error during upload' };
    }
  }

  // Search documents
  async search(query: string): Promise<ApiResponse<SearchResult[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/search?q=${encodeURIComponent(query)}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  // List all tags
  async listTags(): Promise<ApiResponse<string[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/tags`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  // AI-powered semantic search
  async aiSearch(query: string, options?: { bucket?: KnowledgeBucket; maxResults?: number }): Promise<ApiResponse<AISearchResult>> {
    try {
      const response = await fetch(`${API_BASE_URL}/knowledge/ask`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ query, ...options })
      });
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  // Toggle task completion status
  async toggleTask(docId: string, lineIndex: number): Promise<ApiResponse<{ content: string }>> {
    try {
      const response = await fetch(`${API_BASE_URL}/docs/${docId}/toggle-task`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ lineIndex })
      });
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      return { success: false, error: 'Network error' };
    }
  }
}

export const api = new Api();